/**
 * EmptyState — placeholder para listas/tablas sin resultados.
 *
 * Props:
 *   icon?   — componente lucide (opcional, 40px, opacity-30)
 *   title   — string principal
 *   hint?   — React node (texto secundario o JSX con llamada a acción)
 *   action? — botón o nodo final (por debajo del hint)
 *   size?   — 'sm' (py-10) | 'md' (py-16) | 'lg' (py-20, default)
 */
import type { ComponentType, ReactNode } from 'react'

type LucideIconType = ComponentType<{ size?: number | string; className?: string }>

type EmptyStateProps = {
  icon?: LucideIconType
  title: string
  hint?: ReactNode
  action?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const PAD: Record<NonNullable<EmptyStateProps['size']>, string> = {
  sm: 'py-10',
  md: 'py-16',
  lg: 'py-20',
}

const ICON_SIZE: Record<NonNullable<EmptyStateProps['size']>, number> = {
  sm: 28,
  md: 36,
  lg: 40,
}

export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
  size = 'lg',
  className = '',
}: EmptyStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col items-center justify-center ${PAD[size]} text-content-subtle ${className}`}
    >
      {Icon && <Icon size={ICON_SIZE[size]} className="mb-3 opacity-30" />}
      <p className="text-sm font-medium text-content-muted">{title}</p>
      {hint && <div className="text-xs mt-1 text-content-subtle text-center max-w-sm">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}
